import React, { useState, useEffect } from 'react'
import { LoadIndicator } from 'devextreme-react/load-indicator'
import TextBox from 'devextreme-react/text-box'
import Button from 'devextreme-react/button'
import DataGrid, { Column, Scrolling, Paging, Export, Toolbar, Item } from 'devextreme-react/data-grid'
import { saveAs } from 'file-saver'
import axios from 'axios'
import notify from '../../utils/notify'
import { useNavigate } from 'react-router-dom'

export default function GDPR() {
	const navigate = useNavigate()
	const minimumSearchLength = 3
	const [loading, setLoading] = useState(false)
	const [results, setResults] = useState<any[]>([])
	const [recordCount, setRecordCount] = useState(0)
	const [formData, setFormData] = useState({
		firstName: '',
		lastName: '',
		email: '',
		phone: '',
		kid: '',
		city: '',
	})

	useEffect(() => {
		document.title = 'Fiber :: GDPR DSAR Search'
		axios
			.get(`${API_FIBER_URL}/gdpr/has-access`)
			.then(response => {
				if (response.data.hasAccess === false) {
					navigate('/')
				}
			})
			.catch(error => {
				navigate('/')
			})
	}, [navigate])

	const handleSearch = async () => {
		const invalidFields = Object.values(formData).filter(value => {
			const trimmedValue = value.trim()
			return trimmedValue.length > 0 && trimmedValue.length < minimumSearchLength
		})

		if (invalidFields.length > 0) {
			notify(`Please enter at least ${minimumSearchLength} characters in all search fields.`, 'warning')
			return
		}

		setLoading(true)
		try {
			const response = await axios.post(`${API_FIBER_URL}/gdpr/dsar-search`, formData)
			if (response.data.success) {
				setResults(response.data.data || [])
				setRecordCount(response.data.RecordCount || 0)
				if (response.data.data && response.data.data.length === 0) {
					notify('No results found', 'info')
				}
			} else {
				notify('Error during search', 'error')
			}
		} catch (error: any) {
			notify(error.response?.data?.message || 'Error during search', 'error')
			setResults([])
			setRecordCount(0)
		} finally {
			setLoading(false)
		}
	}

	const handleInputChange = (field: string, value: string) => {
		setFormData(prev => ({ ...prev, [field]: value }))
	}

	const handleExporting = (e: any) => {
		if (results.length === 0) {
			notify('No data to export', 'info')
			e.cancel = true
			return
		}

		const columns = Object.keys(results[0] || {})
		const headers = columns.join(',')
		const rows = results.map(row => {
			return columns
				.map(col => {
					const value = row[col]
					if (value === null || value === undefined) return ''
					const stringValue = String(value)
					if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
						return `"${stringValue.replace(/"/g, '""')}"`
					}
					return stringValue
				})
				.join(',')
		})

		const csvContent = [headers, ...rows].join('\n')
		const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
		saveAs(blob, `gdpr-dsar-results-${new Date().toISOString().split('T')[0]}.csv`)

		e.cancel = true
	}

	return (
		<div className="content-block">
			<h2>GDPR DSAR Search</h2>
			<div
				style={{
					marginBottom: '20px',
					padding: '15px',
					border: '1px solid #ddd',
					borderRadius: '5px',
					backgroundColor: 'white',
					boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
				}}
			>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(3, 1fr)',
						gap: '10px',
						marginBottom: '10px',
					}}
				>
					<TextBox
						label="First Name"
						value={formData.firstName}
						onValueChanged={e => handleInputChange('firstName', e.value)}
					/>
					<TextBox
						label="Last Name"
						value={formData.lastName}
						onValueChanged={e => handleInputChange('lastName', e.value)}
					/>
					<TextBox
						label="Email"
						value={formData.email}
						onValueChanged={e => handleInputChange('email', e.value)}
					/>
					<TextBox
						label="Phone"
						value={formData.phone}
						onValueChanged={e => handleInputChange('phone', e.value)}
					/>
					<TextBox label="KID" value={formData.kid} onValueChanged={e => handleInputChange('kid', e.value)} />
					<TextBox
						label="City"
						value={formData.city}
						onValueChanged={e => handleInputChange('city', e.value)}
					/>
				</div>
				<Button text="Search" type="default" onClick={handleSearch} disabled={loading} icon="search" />
			</div>

			{loading && (
				<div style={{ textAlign: 'center', padding: '20px' }}>
					<div style={{ marginBottom: '8px', fontWeight: 500 }}>
						Searching for data based on the information entered in the search fields.
					</div>
					<LoadIndicator height={30} width={30} />
				</div>
			)}

			{!loading && results.length > 0 && (
				<div style={{ marginBottom: '10px', fontWeight: 600 }}>Record Count: {recordCount}</div>
			)}

			{!loading && results.length > 0 && (
				<DataGrid
					dataSource={results}
					showBorders={true}
					rowAlternationEnabled={true}
					height="600px"
					onExporting={handleExporting}
					columnAutoWidth={true}
					allowColumnResizing={true}
				>
					<Export enabled={true} />
					<Toolbar>
						<Item name="exportButton" />
					</Toolbar>
					<Scrolling mode="virtual" />
					<Paging defaultPageSize={50} />
					{Object.keys(results[0] || {}).map(key => (
						<Column key={key} dataField={key} caption={key} />
					))}
				</DataGrid>
			)}
		</div>
	)
}
